import { useEffect, useState } from "react";
import api, { euro, num } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { HardHat, AlertTriangle, Info } from "lucide-react";
import { NONE } from "./constants";

export default function SubappaltoDialog({
  subRiga, setSubRiga, subQta, setSubQta, subAzienda, setSubAzienda, subappaltatori, commessaId, saveSub,
}) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!subRiga || !subAzienda) { setInfo(null); return; }
    let active = true;
    api.get(`/commesse/${commessaId}/subappalto/price`, { params: { subappaltatore_id: subAzienda, codice: subRiga.codice } })
      .then((r) => { if (active) setInfo(r.data); })
      .catch(() => { if (active) setInfo(null); });
    return () => { active = false; };
  }, [subRiga, subAzienda, commessaId]);

  const prezzo = info?.found ? info.price : 0;
  // Blocco: la contabilità di subappalto esiste e ha un listino, ma il codice non c'è.
  const blocked = !!subAzienda && info?.exists && info?.has_listino && !info?.found;
  const qtaNum = parseFloat(subQta) || 0;

  return (
    <Dialog open={!!subRiga} onOpenChange={(o) => !o && setSubRiga(null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><HardHat className="h-5 w-5 text-amber-600" /> Quota in subappalto</DialogTitle>
          <DialogDescription>
            La quota confluisce automaticamente nella contabilità di subappalto del subappaltatore scelto.
          </DialogDescription>
        </DialogHeader>
        {subRiga && (
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Voce di capitolato</div>
              <div className="text-sm font-semibold text-slate-800 mt-0.5">{subRiga.codice} · {subRiga.descrizione}</div>
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Azienda subappaltatrice</Label>
              <Select value={subAzienda || NONE} onValueChange={(v) => setSubAzienda(v === NONE ? "" : v)}>
                <SelectTrigger data-testid="sub-azienda-select" className="mt-1.5 bg-white">
                  <SelectValue placeholder="Seleziona azienda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Nessuna —</SelectItem>
                  {subappaltatori.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Nessuna azienda (aggiungi in Anagrafiche)</div>}
                  {subappaltatori.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.nome}{s.piva ? ` · ${s.piva}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {subAzienda && info && (
              <div className="text-xs">
                {info.found && (
                  <span className="text-slate-500">Prezzo subappalto (da listino): <span className="font-semibold text-amber-700 tabular-nums">{euro(prezzo)}</span> / {subRiga.unita_misura}</span>
                )}
                {blocked && (
                  <span className="inline-flex items-start gap-1.5 text-rose-600 font-medium" data-testid="sub-missing-warning">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    Voce non presente nel listino subappalto di questa azienda. Aggiungila al listino per procedere.
                  </span>
                )}
                {!info.exists && (
                  <span className="inline-flex items-start gap-1.5 text-blue-700" data-testid="sub-autocreate-info">
                    <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    Verrà creata automaticamente la contabilità di subappalto per questa azienda (prezzo da definire nel suo listino).
                  </span>
                )}
                {info.exists && !info.has_listino && (
                  <span className="inline-flex items-start gap-1.5 text-blue-700" data-testid="sub-nolistino-info">
                    <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    La contabilità di subappalto esiste ma non ha ancora un listino: prezzo impostato a 0 (definiscilo nel listino).
                  </span>
                )}
              </div>
            )}
            <div>
              <Label className="text-slate-700 text-sm">Quantità eseguita in subappalto</Label>
              <Input type="number" step="0.01" data-testid="sub-qta-input" value={subQta}
                onChange={(e) => setSubQta(e.target.value)} className="mt-1.5" placeholder="0" disabled={blocked} />
              <p className="text-xs text-slate-400 mt-1">Max {num(subRiga.quantita)} {subRiga.unita_misura}</p>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-amber-50 px-4 py-3">
              <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Importo subappalto</span>
              <span data-testid="sub-importo-preview" className="font-heading font-bold text-amber-700 tabular-nums">
                {euro(qtaNum * prezzo)}
              </span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setSubRiga(null)}>Annulla</Button>
          <Button data-testid="save-sub-button" onClick={saveSub} disabled={blocked} className="bg-amber-600 hover:bg-amber-700 text-white">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
